import { useMemo, useState } from 'react'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isWithinInterval } from 'date-fns'
import { ChevronLeft, ChevronRight, MapPin } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Link } from 'react-router-dom'
import { parseLocalDate } from '@/modules/trips/utils/dateHelpers'

interface Trip {
  id: string
  name: string
  start_date: string | null
  end_date: string | null
  status?: string
}

interface TripCalendarProps {
  trips: Trip[]
}

export function TripCalendar({ trips }: TripCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  // Get trips for a specific date
  const getTripsForDate = (date: Date) => {
    return trips.filter(trip => {
      if (!trip.start_date) return false
      
      try {
        // Parse dates as local dates to avoid timezone issues
        const startDate = parseLocalDate(trip.start_date)
        
        // If trip has an end date, check if the date falls within the trip range
        if (trip.end_date) {
          const endDate = parseLocalDate(trip.end_date)
          return isWithinInterval(date, { start: startDate, end: endDate })
        }
        
        // Otherwise, only show on start date
        return isSameDay(startDate, date)
      } catch {
        // If parsing fails, fall back to simple comparison
        return false
      }
    })
  }

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  // Navigate months
  const previousMonth = () => setCurrentDate(subMonths(currentDate, 1))
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1))
  const goToToday = () => setCurrentDate(new Date())

  const today = new Date()
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div>
      <div className="pb-2 mb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-earth-900 dark:text-foreground">
            {format(currentDate, 'MMMM yyyy')}
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={previousMonth}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToToday}
              className="h-7 px-2 text-[10px]"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextMonth}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
      <div className="pt-0">
        <div className="w-full">
          {/* Day labels */}
          <div className="grid grid-cols-7 gap-0.5 mb-1.5">
            {dayLabels.map(day => (
              <div
                key={day}
                className="text-center text-[10px] font-medium text-earth-600 dark:text-earth-400 py-0.5"
              >
                {day.substring(0, 1)}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarDays.map((day, idx) => {
              const isCurrentMonth = isSameMonth(day, currentDate)
              const isToday = isSameDay(day, today)
              const dayTrips = getTripsForDate(day)

              return (
                <div
                  key={idx}
                  className={`min-h-[48px] p-0.5 border border-earth-200 dark:border-earth-700 rounded ${
                    isCurrentMonth ? 'bg-card' : 'bg-earth-100 dark:bg-earth-800'
                  } ${isToday ? 'ring-1 ring-adventure-500' : ''}`}
                >
                  <div
                    className={`text-[10px] font-medium mb-0.5 ${
                      isCurrentMonth
                        ? isToday
                          ? 'text-adventure-600 dark:text-adventure-400 font-bold'
                          : 'text-earth-900 dark:text-foreground'
                        : 'text-earth-400 dark:text-earth-500'
                    }`}
                  >
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayTrips.slice(0, 1).map(trip => (
                      <Link
                        key={trip.id}
                        to={`/trips/${trip.id}`}
                        className="block text-[9px] px-0.5 py-0.5 rounded bg-adventure-100 dark:bg-adventure-900 text-adventure-700 dark:text-adventure-300 hover:bg-adventure-200 dark:hover:bg-adventure-800 truncate transition-colors"
                        title={trip.name}
                      >
                        {trip.name.length > 8 ? `${trip.name.substring(0, 7)}...` : trip.name}
                      </Link>
                    ))}
                    {dayTrips.length > 1 && (
                      <div className="text-[9px] text-earth-600 dark:text-earth-400 px-0.5">
                        +{dayTrips.length - 1}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

